import { useEffect, useState } from "react";
import { hivesApi } from "../../services/hivesApi";
import { useToast } from "../../context/ToastContext";
import { Card } from "../../components/common/Card";
import { Button } from "../../components/common/Button";
import { DataTable } from "../../components/common/DataTable";
import { formatDateOnly } from "../../services/hiveActions";
import { HivePicker } from "../../components/common/HivePicker";

/** Log new splits/swarm-captures/merges and show past ones (GET
 * /hives/{id}/colony-events, added alongside the three POST endpoints). */
export function ColonyEventsTab({ hive, onLogged, onMerged }) {
  const { notify } = useToast();
  // "new" creates the daughter, which is what splitting normally means;
  // "existing" is for somebody who already made the hive by hand. The
  // endpoint took only the second, so a split logged an event and produced
  // no hive at all.
  const [splitForm, setSplitForm] = useState({
    mode: "new", daughter_hive_number: "", daughter_hive_id: "",
    method: "Walkaway", queen_status: "New",
  });
  const [swarmForm, setSwarmForm] = useState({ capture_method: "Shake" });
  const [mergeForm, setMergeForm] = useState({ target_hive_id: "", method: "Newspaper" });
  const [events, setEvents] = useState(null);

  async function loadEvents() {
    const r = await hivesApi.colonyEvents(hive.hive_id);
    setEvents(r);
  }
  useEffect(() => { loadEvents(); }, [hive.hive_id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function logSplit() {
    try {
      const isNew = splitForm.mode === "new";
      if (isNew && !splitForm.daughter_hive_number.trim()) {
        return notify("Give the new hive a number", "warning");
      }
      if (!isNew && !splitForm.daughter_hive_id) {
        return notify("Pick the hive the split went into", "warning");
      }
      const r = await hivesApi.split(hive.hive_id, {
        ...(isNew
          ? { daughter_hive_number: splitForm.daughter_hive_number.trim() }
          : { daughter_hive_id: Number(splitForm.daughter_hive_id) }),
        split_date: new Date().toISOString().slice(0, 10),
        method: splitForm.method,
        queen_status: splitForm.queen_status,
      });
      notify(r.daughter_created ? "Split logged and the new hive created" : "Split logged", "success");
      setSplitForm((f) => ({ ...f, daughter_hive_number: "", daughter_hive_id: "" }));
      loadEvents();
      onLogged?.();
    } catch (err) {
      notify(err.apiMessage || "Could not log split", "danger");
    }
  }

  async function logSwarm() {
    try {
      await hivesApi.swarmCapture(hive.hive_id, { capture_date: new Date().toISOString().slice(0, 10), capture_method: swarmForm.capture_method });
      notify("Swarm capture logged", "success");
      loadEvents();
      onLogged?.();
    } catch (err) {
      notify(err.apiMessage || "Could not log swarm capture", "danger");
    }
  }

  async function logMerge() {
    const targetId = Number(mergeForm.target_hive_id);
    try {
      await hivesApi.merge(hive.hive_id, { target_hive_id: targetId, merge_date: new Date().toISOString().slice(0, 10), method: mergeForm.method });
      notify("Merged. This hive is retired — showing the one it went into.", "success");
      onLogged?.();
      // Follow the bees. A successful merge RETIRES the hive being
      // viewed, so staying here leaves the keeper on a dead record
      // wondering whether it worked -- the colony is in the target now.
      //
      // Handed UP rather than navigated from here: a tab that knows about
      // routes cannot be rendered without one, and this component is used
      // in tests and could be used in a modal.
      if (targetId) onMerged?.(targetId);
      else loadEvents();
    } catch (err) {
      notify(err.apiMessage || "Could not log merge", "danger");
    }
  }

  return (
    <div style={{ display: "grid", gap: "var(--space-4)" }}>
      <Card title="Log a Split">
        <div style={{ display: "grid", gap: "var(--space-2)", maxWidth: 320 }}>
          {/* The new hive the split goes into. Was a typed id; the two
              hives in a split are easy to transpose when both are numbers. */}
          <label>
            Daughter hive
            <select id="colony-events-tab-mode" name="mode" className="touch-target" value={splitForm.mode}
              onChange={(e) => setSplitForm((f) => ({ ...f, mode: e.target.value }))}>
              <option value="new">Create a new hive</option>
              <option value="existing">Into a hive I already made</option>
            </select>
          </label>
          {splitForm.mode === "new" ? (
            <label>
              New hive number
              <input id="colony-events-tab-daughter-hive-number" name="daughter-hive-number" className="touch-target" value={splitForm.daughter_hive_number}
                placeholder="e.g. 12"
                onChange={(e) => setSplitForm((f) => ({ ...f, daughter_hive_number: e.target.value }))} />
            </label>
          ) : (
            <HivePicker label="Daughter hive" value={splitForm.daughter_hive_id} onChange={(v) => setSplitForm((f) => ({ ...f, daughter_hive_id: v }))} />
          )}
          <select id="colony-events-tab-method" name="method" value={splitForm.method} onChange={(e) => setSplitForm((f) => ({ ...f, method: e.target.value }))} className="touch-target">
            {["Walkaway", "Push", "Pull", "Other"].map((m) => <option key={m}>{m}</option>)}
          </select>
          <select id="colony-events-tab-queen-status" name="queen-status" value={splitForm.queen_status} onChange={(e) => setSplitForm((f) => ({ ...f, queen_status: e.target.value }))} className="touch-target">
            {["New", "Bought", "Existing"].map((m) => <option key={m}>{m}</option>)}
          </select>
          <Button onClick={logSplit}>Log Split</Button>
        </div>
      </Card>

      <Card title="Log a Swarm Capture">
        <div style={{ display: "grid", gap: "var(--space-2)", maxWidth: 320 }}>
          <select id="colony-events-tab-capture-method" name="capture-method" value={swarmForm.capture_method} onChange={(e) => setSwarmForm((f) => ({ ...f, capture_method: e.target.value }))} className="touch-target">
            {["Trap", "Shake", "Other"].map((m) => <option key={m}>{m}</option>)}
          </select>
          <Button onClick={logSwarm}>Log Swarm Capture</Button>
        </div>
      </Card>

      <Card title="Log a Merge">
        <div style={{ display: "grid", gap: "var(--space-2)", maxWidth: 320 }}>
          {/* Merging into the WRONG hive is destructive and not obviously
              reversible, which is the strongest argument for a picker here. */}
          <HivePicker label="Merge into" value={mergeForm.target_hive_id} onChange={(v) => setMergeForm((f) => ({ ...f, target_hive_id: v }))} />
          <select id="colony-events-tab-method-2" name="method" value={mergeForm.method} onChange={(e) => setMergeForm((f) => ({ ...f, method: e.target.value }))} className="touch-target">
            {["Newspaper", "Smoke", "Other"].map((m) => <option key={m}>{m}</option>)}
          </select>
          <Button onClick={logMerge}>Log Merge</Button>
        </div>
      </Card>

      {/*
        * Three tables used to sit in one "History" card with no headings
        * between them, so a merge read as "hive number, date, method" and
        * nothing said it WAS a merge. Reported as "the merge is ambiguous,
        * there's nothing in the log that says it's a merge".
        *
        * Each event type gets its own titled card, each row says what
        * happened in a sentence, and hive NUMBERS replace the raw ids --
        * "Target Hive 14" is a database key, not something a beekeeper
        * recognises.
        */}
      <Card title="Splits" className="section-spacing">
        <DataTable
          rows={events?.splits}
          loading={!events}
          rowKey="split_id"
          emptyTitle="No splits logged"
          emptyDescription="Splitting this hive, or splitting another one into it, shows up here."
          columns={[
            { key: "split_date", header: "When", render: (r) => formatDateOnly(r.split_date) },
            {
              key: "what",
              header: "What happened",
              render: (r) => (r.parent_hive_id === hive.hive_id
                ? `Split into Hive ${r.daughter_hive_number ?? r.daughter_hive_id}`
                : `Came from a split of Hive ${r.parent_hive_number ?? r.parent_hive_id}`),
            },
            { key: "method", header: "Method" },
            { key: "queen_status", header: "Queen" },
          ]}
        />
      </Card>

      <Card title="Swarm captures" className="section-spacing">
        <DataTable
          rows={events?.swarm_captures}
          loading={!events}
          rowKey="capture_id"
          emptyTitle="No swarm captures logged"
          emptyDescription="A swarm hived into this colony shows up here."
          columns={[
            { key: "capture_date", header: "When", render: (r) => formatDateOnly(r.capture_date) },
            { key: "what", header: "What happened", render: () => "Swarm captured into this hive" },
            { key: "capture_method", header: "Method" },
            { key: "swarm_weight", header: "Weight", render: (r) => (r.swarm_weight != null ? r.swarm_weight : "—") },
          ]}
        />
      </Card>

      <Card title="Merges" className="section-spacing">
        <DataTable
          rows={events?.merges}
          loading={!events}
          rowKey="merge_id"
          emptyTitle="No merges logged"
          emptyDescription="Combining this hive with another one shows up here."
          columns={[
            { key: "merge_date", header: "When", render: (r) => formatDateOnly(r.merge_date) },
            {
              key: "what",
              header: "What happened",
              // Which side of the merge this hive was on is the whole
              // point: one of them stopped existing.
              render: (r) => (r.source_hive_id === hive.hive_id
                ? `Merged into Hive ${r.target_hive_number ?? r.target_hive_id} — this hive was retired`
                : `Hive ${r.source_hive_number ?? r.source_hive_id} was merged into this one`),
            },
            { key: "method", header: "Method" },
          ]}
        />
      </Card>
    </div>
  );
}
